import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DoesNotExistAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { PaymentMethod, StudentType } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { from, switchMap } from 'rxjs';
import { Course } from '../../courses/Course.js';
import { ChancenwerkUserFilter } from '../../filter/ChancenwerkUserFilter.js';
import { KPFAspect } from '../../master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '../../master-data-management/users/api/UserAPI.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '../../master-data-management/users/entities/Student.js';
import { OrAspect } from '../../OrAspect.js';
import { PresenceRecord, Status } from '../../PresenceRecord.js';
import { KPIKPFReport } from '../entities/KPIKPFReport.js';

export namespace KPIAPI {
  export const ensureLastMonth = async (skipIfPresent?: boolean) => {
    const lastMonth = DateTime.now().minus({ month: 1 }).startOf('month');
    const allKpfIDs = (await loadMongoData(KPF, {}, { projection: { _id: 1 } })).map(kpf => kpf._id);
    const userMatCol = await getRawCollection<ChancenwerkUser>('adornisuser-materialized');
    const kpiCol = await getRawCollection<KPIKPFReport>(KPIKPFReport._collectionName);

    if (skipIfPresent && (await kpiCol.countDocuments({})) > 0) {
      return;
    }

    for (const kpfId of allKpfIDs) {
      const baseAspects = [
        new KPFAspect({
          customID: 'kpfs',
          value: kpfId,
        }),
        new BooleanAspect({
          customID: 'active',
          value: true,
        }),
        new BooleanAspect({
          fieldName: 'guestAccount',
          value: false,
        }),
        new OrAspect({
          customID: 'or',
          aspects: [
            new StringAspect({
              fieldName: 'bankInformation.zahlungsart',
              mode: 'containsOR',
              value: Object.values(PaymentMethod)
                .filter(
                  v =>
                    v != PaymentMethod.SPENDENEINZUG &&
                    v != PaymentMethod.WARTELISTE &&
                    v != PaymentMethod.DIGITAL_NOT_FINISHED,
                )
                .join(','),
            }),
            new DoesNotExistAspect({
              fieldName: 'bankInformation.zahlungsart',
            }),
          ],
        }),
      ];

      const activeUS = await UserAPI.getCountByFilter(
        new ChancenwerkUserFilter({
          _class: Student._class,
          aspects: [
            new StringAspect({
              fieldName: 'schuelertyp',
              mode: 'equals',
              value: StudentType.US,
            }),
            ...baseAspects,
          ],
        }),
      )();

      const activeOS = await UserAPI.getCountByFilter(
        new ChancenwerkUserFilter({
          _class: Student._class,
          aspects: [
            new StringAspect({
              fieldName: 'schuelertyp',
              mode: 'equals',
              value: StudentType.OS,
            }),
            ...baseAspects,
          ],
        }),
      )();

      const newRegistrations = await userMatCol.countDocuments({
        _class: Student._class,
        guestAccount: false,
        schuelertyp: StudentType.US,
        $or: [{ 'activeRelations.objectID': kpfId }, { 'inactiveRelations.objectID': kpfId }],
        'joinInformation.eintrittsdatum': {
          $gte: DateTime.fromObject({
            year: 2024,
            month: 7,
            day: 1,
          }).toJSDate(),
        },
      });

      const courses = await loadMongoData(Course, {
        kpfID: kpfId,
      });
      const coursesById = courses.reduce(
        (acc, course) => {
          acc[course._id] = course;
          return acc;
        },
        {} as Record<string, EntityData<Course>>,
      );

      const records = await loadMongoData(PresenceRecord, {
        courseID: {
          $in: courses.map(c => c._id),
        },
        date: {
          $gte: lastMonth.startOf('month').toJSDate(),
          $lt: lastMonth.endOf('month').toJSDate(),
        },
      });

      const attendance = {
        sum: 0,
        present: 0,
      };
      for (const record of records) {
        const course = coursesById[record.courseID ?? ''];
        if (!course) {
          continue;
        }

        attendance.sum += /*course.osStudentIDs.length +*/ course.usStudentIDs.length;
        // attendance.present += record.studentRecordsOS?.filter(r => r.status === Status.PRESENT).length ?? 0;
        attendance.present += record.studentRecordsUS?.filter(r => r.status === Status.PRESENT).length ?? 0;
      }

      await kpiCol.updateOne(
        {
          _id: `${kpfId}-${lastMonth.year}-${lastMonth.month}`,
          kpfId,
          year: lastMonth.year,
          month: lastMonth.month,
        },
        {
          $set: {
            _class: KPIKPFReport._class,
            kpfId,
            year: lastMonth.year,
            month: lastMonth.month,
            activeUS,
            activeOS,
            newRegistrations,
            attendancePercent: attendance.present / attendance.sum || 0,
          },
        },
        {
          upsert: true,
        },
      );

      logger.info(
        {
          activeUS,
          activeOS,
          newRegistrations,
          attendancePercent: attendance.present / attendance.sum || 0,
          attendanceSum: attendance.sum,
          attendancePresent: attendance.present,
        },
        `Updated KPI for KPF ${kpfId} in month ${lastMonth.month}/${lastMonth.year}`,
      );
    }
  };

  export const subscribe = registerSubscription({
    operationName: 'KPIAPI_subscribe',
    type: () => [KPIKPFReport],
    resolve: (skip: number, limit: number, filter: AdornisFilter) => {
      return () => {
        return from(
          (async () => {
            return (await loadMongoData(KPF, {}, { projection: { _id: 1 } })).map(kpf => kpf._id);
          })(),
        ).pipe(
          switchMap(async kpfIds => {
            const now = DateTime.now().minus({ month: 1 });
            const col = await getRawCollection<KPIKPFReport>(KPIKPFReport._collectionName);
            const res = await col
              .find(
                {
                  kpfId: { $in: kpfIds },
                  year: now.year,
                  month: now.month,
                  $or: [{ active: { $gt: 0 } }, { newRegistrations: { $gt: 0 } }, { attendancePercent: { $gt: 0 } }],
                },
                {
                  sort: {
                    ...(filter.getMongoOptions().sort ? { ...filter.getMongoOptions().sort } : { activeUS: -1 }),
                  },
                  skip: skip,
                  limit: limit,
                },
              )
              .toArray();
            return res;
          }),
        );
      };
    },
    params: [
      { type: () => Int, name: 'skip' },
      { type: () => Int, name: 'limit' },
      { type: () => AdornisFilter, name: 'filter' },
    ],
  });
}
